/**
 * Offers management page
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOffer } from '@/contexts/OfferContext';
import { OfferCard } from '@/components/offers/OfferCard';
import { LoadingState } from '@/components/shared/LoadingState';
import { Button } from '@/components/ui/Button';
import { toast } from '@/lib/toast';

export default function OffersPage() {
  const navigate = useNavigate();
  const { offers, listOffers, deleteOffer } = useOffer();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    setLoading(true);
    try {
      await listOffers();
    } catch (error) {
      console.error('Error loading offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (offerId: string) => {
    try {
      await deleteOffer(offerId);
      toast.success('Oferta eliminada exitosamente');
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Error al eliminar la oferta');
    }
  };

  if (loading) {
    return <LoadingState message="Loading offers..." />;
  }

  return (
    <div className="min-h-screen bg-surface-2">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-ink mb-2">
              Offers & Promotions
            </h1>
            <p className="text-ink-muted">
              Manage your promotional offers and discounts
            </p>
          </div>
          <Button
            onClick={() => navigate('/offers/new')}
            className="bg-accent hover:bg-accent/90 text-on-accent"
          >
            + New Offer
          </Button>
        </div>

        {/* Offers Grid */}
        {offers && offers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <div key={offer.offerId} className="relative group">
                <OfferCard offer={offer} />
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/offers/${offer.offerId}/edit`)}
                      className="px-3 py-1 bg-info hover:bg-info/90 text-white text-sm rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(offer.offerId)}
                      className="px-3 py-1 bg-danger hover:bg-danger/90 text-white text-sm rounded"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-surface rounded-lg shadow p-12 text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-xl font-semibold text-ink mb-2">
              No offers yet
            </h3>
            <p className="text-ink-muted mb-6">
              Create your first promotional offer to attract more customers
            </p>
            <Button
              onClick={() => navigate('/offers/new')}
              className="bg-accent hover:bg-accent/90 text-on-accent"
            >
              Create First Offer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
